import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray
} from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChildren
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { SortQualifier } from '@core/filters/path-filter/dsl/search-bar';
import { FieldType } from '@core/filters/path-filter/vocabulary/vocabulary';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';

import { SelectItem, SelectValue } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { PATH_FILTER_I18N } from '../../path-filter-labels';
import { SortOption, SortTarget, sameSortTarget } from '../sort-options';

let nextId = 0;

type DirectionWords = 'text' | 'number' | 'duration' | 'date' | 'default';

function directionWords(type: FieldType | null): DirectionWords {
  switch (type) {
    case 'TEXT':
    case 'UID':
      return 'text';
    case 'NUMBER':
    case 'INTEGER':
    case 'MASS':
    case 'LENGTH':
      return 'number';
    case 'DURATION':
      return 'duration';
    case 'DATE':
    case 'DATE_TIME':
      return 'date';
    default:
      return 'default';
  }
}

/**
 * One aggregate of a to-many leaf already decides the order; its sibling would only be a tie-break
 * on the same rows read the other way.
 */
function taken(sort: SortQualifier[], target: SortTarget): boolean {
  return sort.some(
    (qualifier) =>
      sameSortTarget(qualifier, target) ||
      (qualifier.field === target.field &&
        qualifier.association?.aggregate !== undefined &&
        target.association?.aggregate !== undefined &&
        qualifier.association.edge === target.association.edge)
  );
}

interface MatchedOption {
  option: SortOption;
  before: string;
  hit: string;
  after: string;
}

@Component({
  selector: 'app-path-filter-sort-tab',
  imports: [TranslatePipe, SelectComponent, CdkDropList, CdkDrag, CdkDragHandle],
  templateUrl: './sort-tab.component.html',
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  `
})
export class SortTabComponent {
  options = input.required<SortOption[]>();
  sort = input.required<SortQualifier[]>();
  sortChange = output<SortQualifier[]>();

  protected readonly ICONS = ICONS;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly injector = inject(Injector);
  private readonly handles = viewChildren<ElementRef<HTMLButtonElement>>('handle');
  protected readonly idPrefix = `sort-tab-${nextId++}`;

  protected readonly query = signal('');

  protected readonly active = computed(() => {
    this.currentLang();
    return this.sort().map((qualifier, index) => {
      const option = this.options().find((candidate) =>
        sameSortTarget(candidate.target, qualifier)
      );
      const words = directionWords(option?.type ?? null);
      const direction = (order: 'asc' | 'desc'): SelectItem => ({
        id: order,
        label: this.translate.instant(
          `${PATH_FILTER_I18N}.sort.directions.${words}.${order}`
        )
      });
      return {
        index,
        order: qualifier.order,
        label: option?.label ?? qualifier.field,
        kpi: option?.kpi ?? false,
        directions: [direction('asc'), direction('desc')]
      };
    });
  });

  /** Targets already sorted on are edited in the list above rather than offered twice. */
  protected readonly groups = computed(() => {
    const needle = this.query().trim().toLowerCase();
    const groups = new Map<string, MatchedOption[]>();
    for (const option of this.options()) {
      if (taken(this.sort(), option.target)) continue;
      const at = option.label.toLowerCase().indexOf(needle);
      if (at === -1) continue;
      const group = groups.get(option.group) ?? [];
      group.push({
        option,
        before: option.label.slice(0, at),
        hit: option.label.slice(at, at + needle.length),
        after: option.label.slice(at + needle.length)
      });
      groups.set(option.group, group);
    }
    return [...groups].map(([name, options]) => ({ name, options }));
  });

  protected add(option: SortOption): void {
    this.sortChange.emit([...this.sort(), { ...option.target, order: 'asc' }]);
  }

  protected drop(event: CdkDragDrop<unknown>): void {
    this.move(event.previousIndex, event.currentIndex);
  }

  /** The keyboard path for dragging: the focus follows the entry it moves. */
  protected onHandleKey(event: KeyboardEvent, index: number): void {
    const step = { ArrowUp: -1, ArrowDown: 1 }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const to = index + step;
    if (to < 0 || to >= this.sort().length) return;
    this.move(index, to);
    afterNextRender(() => this.handles()[to]?.nativeElement.focus(), {
      injector: this.injector
    });
  }

  private move(from: number, to: number): void {
    const sort = [...this.sort()];
    moveItemInArray(sort, from, to);
    this.sortChange.emit(sort);
  }

  protected setOrder(index: number, order: SelectValue): void {
    if (order !== 'asc' && order !== 'desc') return;
    this.sortChange.emit(
      this.sort().map((qualifier, position) =>
        position === index ? { ...qualifier, order } : qualifier
      )
    );
  }

  protected remove(index: number): void {
    this.sortChange.emit(this.sort().filter((_, position) => position !== index));
  }
}
